import React from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';
import WidgetHeader from '../Widget/WidgetHeader';

import ToasterAlert from '../../components/Donation/ToasterAlert';
import CssTransition from '../../components/Transition/CssTransition';
import FindRepForm from '../../components/LocalRep/FindRepForm';
import RoundImage from '../../components/Image/RoundImage';
import TransitionContainer from '../../components/Transition/TranstionContainer';

import { onGoToNextActionInsideCard } from '../Widget/actions';
import { hideFindRepCard, loadLocalRep } from '../LocalRep/actions';

import { copyFor } from '../../copy/copy';
import { DefaultRepImageURL } from '../../containers/DefaultStyles/Shared';

import { pictureUrl } from '../../utils/imageLoading';

const Container = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
`;

const Body = styled.div`
    flex: 1;
    margin: 1em 1em 0.4375em;
    display: flex;
    flex-direction: column;
`;

const Content = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    margin-top: 1.5em;
`;

const Sidebar = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    flex: 0 0 30%;
    margin-right: 1em;

    @media (max-width: 30em) {
        display: none;
    }
`;

const ImageContainer = styled.div`
    display: flex;
    margin-top: 0.5em;
`;

const SidebarTitle = styled.h3`
    font-size: 1em;
    font-weight: bold;
    line-height: 1.25;
    color: #000;
    text-transform: capitalize;
    margin-top: 1em;
    margin-bottom: 0.125em;
`;

const SidebarDescription = styled.p`
    font-size: 0.875em;
    line-height: 1.43;
    color: #333;
`;

const ContentContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;

    form {
        padding-bottom: 10px;
    }
`;

const ContentTitle = styled.h2`
    font-size: 1.125em;
    font-weight: bold;
    line-height: 1.33;
    margin: 0 0 0.4444em;
`;

const ContentDescription = styled.div`
    object-fit: contain;
    font-family: Lato;
    font-size: 1em;
    line-height: 1.5;
    color: #000;
    margin-bottom: 1em;
`;

const nextActionDescriptionMapping = {
    call: copyFor('findReps', 'instructions', 'call'),
    email: copyFor('findReps', 'instructions', 'email'),
    tweet: copyFor('findReps', 'instructions', 'tweet'),
    petition: copyFor('findReps', 'instructions', 'petition')
};

const FindLocalRep = props => (
    <TransitionContainer zIndex={12}>
        <Container>
            <Overlay />
            <OverlayPublisher />
            <OverlayPartner transitionType="right-to-left" />
            <TransitionGroup>
                {props.addressValidationError && (
                    <CssTransition key="alert" in={props.addressValidationError}>
                        <ToasterAlert content={props.addressValidationError} />
                    </CssTransition>
                )}
            </TransitionGroup>
            <Body>
                <WidgetHeader />
                <Content>
                    <Sidebar>
                        <ImageContainer>
                            <RoundImage
                                src={
                                    props.actions.length < 2
                                        ? props.pictureUrl || DefaultRepImageURL(props.country)
                                        : DefaultRepImageURL(props.country)
                                }
                            />
                        </ImageContainer>
                        <SidebarTitle>
                            {copyFor('findReps', 'contactRep', props.nextActionType)}
                        </SidebarTitle>
                        <SidebarDescription>
                            {nextActionDescriptionMapping[props.nextActionType]}
                        </SidebarDescription>
                    </Sidebar>
                    <ContentContainer>
                        <ContentTitle>{copyFor('findReps', 'title')}</ContentTitle>
                        <ContentDescription
                            className="ie-object-fit-contain-polyfill">
                            {copyFor('findReps', 'provideAddress')}
                        </ContentDescription>
                        <FindRepForm
                            onBack={() =>
                                props.hideFindRepCard(
                                    props.cardType,
                                    props.nextActionType
                                )
                            }
                            submitting={props.isLocalRepPopupLoading}
                            handleSubmit={event => {
                                event.preventDefault();
                                props
                                    .loadLocalRep(
                                        props.endUserLocation,
                                        props.address,
                                        props.representativeSearch,
                                        true
                                    )
                                    .then(() => {
                                        props.nextActionType === 'tweet'
                                            ? props.hideFindRepCard(
                                                  props.cardType,
                                                  props.nextActionType
                                              )
                                            : props.onGoToNextActionInsideCard(
                                                  props.nextActionIdentifier,
                                                  props.nextActionType,
                                                  false,
                                                  false
                                              );
                                    })
                                    .catch(() =>
                                        console.log('Representatives not found!')
                                    );
                            }}
                        />
                    </ContentContainer>
                </Content>
            </Body>
        </Container>
    </TransitionContainer>
);

FindLocalRep.propTypes = {
    address: PropTypes.object.isRequired,
    addressValidationError: PropTypes.any,
    actions: PropTypes.array.isRequired,
    cardType: PropTypes.number.isRequired,
    country: PropTypes.string,
    hideFindRepCard: PropTypes.func.isRequired,
    isLocalRepPopupLoading: PropTypes.bool.isRequired,
    loadLocalRep: PropTypes.func.isRequired,
    nextActionIdentifier: PropTypes.string,
    nextActionType: PropTypes.string,
    onGoToNextActionInsideCard: PropTypes.func.isRequired,
    pictureUrl: PropTypes.string,
    representativeSearch: PropTypes.object,
    endUserLocation: PropTypes.object
};

const getNextActionType = state => {
    const action = state.widget.actions.find(
        a => a.identifier === state.localRep.nextActionIdentifier
    );
    return action != null ? action.type : null;
};

export default connect(
    state => ({
        address: state.form.google_autocomplete
            ? state.form.google_autocomplete.values
            : {},
        addressValidationError: state.widget.addressValidationError,
        country:
            ((state.form.google_autocomplete || {}).values || {}).country ||
            ((state.localRep.localRepData || {}).address || {}).country,
        nextActionType: getNextActionType(state),
        nextActionIdentifier: state.localRep.nextActionIdentifier,
        pictureUrl: pictureUrl(state) && pictureUrl(state)[0],
        isLocalRepPopupLoading: state.localRep.isLocalRepPopupLoading,
        cardType: state.widget.cards[state.widget.activeAction.order - 1].type,
        actions: state.widget.actions
            .filter(function(a) {
                return state.widget.activeAction.type === 'petition'
                    ? a.id === state.widget.activeAction.id
                    : a.order === state.widget.activeAction.order;
            })
            .slice(0, 3)
            .sort(a => a.label),
        representativeSearch: state.widget.maximumRepresentativeSearch,
        endUserLocation: state.widget.endUserLocation
    }),
    {
        hideFindRepCard,
        loadLocalRep,
        onGoToNextActionInsideCard
    }
)(FindLocalRep);
